// One term row. Optimistic UI — clicks mutate the Zustand store
// immediately; the debounced save fires under the row's awareness.

import { useState } from "react";
import type { DocJSON, GlossaryEntry } from "../data/types";
import { useReviewStore } from "./store";
import { SourceSpan } from "./SourceSpan";
import { ReviewEditor } from "./ReviewEditor";
import type { ReviewAction } from "./types";
import { OriginPill } from "../ui";
import { disclose } from "../reading/termOrigin";

interface Props {
  doc: DocJSON;
  entry: GlossaryEntry;
}

export function ReviewRow({ doc, entry }: Props) {
  const action = useReviewStore((s) => s.actions[entry.term_id]);
  const setAction = useReviewStore((s) => s.setAction);
  const removeAdded = useReviewStore((s) => s.removeAdded);
  const [editing, setEditing] = useState(false);

  const status: ReviewAction = action?.action ?? "pending";

  // The displayed definition reflects edits; otherwise the original.
  const displayDef = action?.edited_definition ?? entry.definition;
  const displayKind = action?.edited_kind ?? entry.kind;
  // `?? []` is not defensive padding. `alternates` is declared required on
  // GlossaryEntry, and the seeded fixture documents all carry it, but the real
  // OpenAPI ingestion emits entries without one — so `displayAlts.length` below
  // threw `Cannot read properties of undefined` and the whole review screen
  // rendered an error boundary. Found by the walkthrough gate driving a live
  // document; every unit test used a fixture that had the field.
  const displayAlts = action?.edited_alternates ?? entry.alternates ?? [];
  // …and so does the displayed ORIGIN. A row whose definition a reviewer
  // rewrote must not keep disclosing "extracted by mAiSight": PRD 11's whole
  // job is answering "did mAiSight write this, or did a person?", and on an
  // edited row the honest answer is `user` -> `edit` / "edited by reviewer".
  // Display only, exactly like the three lines above — `entry.origin` is not
  // mutated, the store is untouched, and the published payload is unchanged,
  // so discarding the edit reverts the pill along with the definition.
  const displayOrigin = status === "edited" ? "user" : entry.origin;

  const onAccept = () => setAction(entry.term_id, "accepted");
  const onReject = () => {
    if (entry.origin === "user") {
      removeAdded(entry.term_id);
    } else {
      setAction(entry.term_id, "rejected");
    }
  };
  const onEdit = () => setEditing(true);

  return (
    <div
      className={`maisight-review-row maisight-review-row--${status}`}
      data-testid={`row-${entry.term_id}`}
      data-term={entry.term}
      data-term-id={entry.term_id}
      data-status={status}
      data-origin={entry.origin}
    >
      <div className="maisight-review-row-head">
        <div className="maisight-review-row-title">
          <span className="maisight-review-row-term">{entry.term}</span>
          {/* Origin disclosure is a PRD 11 product invariant and the pill
              text is LOCKED — `spec` / `AI` / `edit`, as TEXT, never a glyph.
              This row used to render its own `OpenAPI` / `LLM` / `User` map,
              which no test pinned and which violated the invariant. The
              strings now come from `reading/termOrigin.ts` via `OriginPill`,
              the single place the reader, the widget and the dashboard all
              read them from. Figma's `Badge/Origin` (23:10) draws these
              uppercase; PRD 18 US-121 overrules that, on purpose. */}
          <OriginPill origin={displayOrigin} />
          <span className="maisight-review-kind">[{displayKind}]</span>
          {status !== "pending" ? (
            <span className={`maisight-review-status maisight-review-status--${status}`}>
              {status}
            </span>
          ) : null}
        </div>
        <div className="maisight-review-row-actions">
          {status !== "accepted" && status !== "added" ? (
            <button
              type="button"
              className="maisight-review-btn maisight-review-btn--accept"
              onClick={onAccept}
              aria-label={`Accept ${entry.term}`}
            >
              Accept
            </button>
          ) : null}
          {status !== "rejected" ? (
            <button
              type="button"
              className="maisight-review-btn maisight-review-btn--ghost"
              onClick={onEdit}
              aria-label={`Edit ${entry.term}`}
            >
              Edit
            </button>
          ) : null}
          {status !== "rejected" ? (
            <button
              type="button"
              className="maisight-review-btn maisight-review-btn--reject"
              onClick={onReject}
              aria-label={`Reject ${entry.term}`}
            >
              Reject
            </button>
          ) : (
            <button
              type="button"
              className="maisight-review-btn maisight-review-btn--ghost"
              onClick={onAccept}
              aria-label={`Restore ${entry.term}`}
            >
              Restore
            </button>
          )}
        </div>
      </div>
      <p className="maisight-review-row-def">{displayDef}</p>
      {/* The attribution footer sits under EVERY definition, not only the
          active row — PRD 11 again, and the same sentence the reading rail
          shows, from the same source. */}
      <p className="maisight-review-row-origin">{disclose(displayOrigin).footerLabel}</p>
      {displayAlts.length > 0 ? (
        <div className="maisight-review-row-alts">
          {displayAlts.map((a) => (
            <span key={a} className="maisight-review-chip maisight-review-chip--readonly">{a}</span>
          ))}
        </div>
      ) : null}
      {entry.origin === "llm" ? <SourceSpan doc={doc} entry={entry} /> : null}
      {editing ? (
        <ReviewEditor
          entry={entry}
          currentDefinition={displayDef}
          currentKind={displayKind}
          currentAlternates={displayAlts}
          onClose={() => setEditing(false)}
        />
      ) : null}
    </div>
  );
}
